import { ExpressionSpecification } from 'mapbox-gl';
import { ComponentProps, useMemo, useState } from 'react';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import MapLocation from '@alltrails/amplitude/enums/MapLocation';
import TrailCardLocation from '@alltrails/amplitude/enums/TrailCardLocation';
import type TrailActivityType from '@alltrails/trails/types/TrailActivityType';
import type InspirationalListType from '@alltrails/types/InspirationalListType';
import useActiveTrailCoordinates from '../../hooks/useActiveTrailCoordinates';
import useShiftKey from '../../hooks/useShiftKey';
import useTrailPinInteractions from '../../hooks/useTrailPinInteractions';
import useTrailPinSvgDefinitions from '../../hooks/useTrailPinSvgDefinitions';
import { useAppDispatch, useAppSelector } from '../../redux';
import { updateActiveTrails } from '../../redux/reducer';
import { buildCommonLayoutProps } from '../../utils/buildPinIconExpressions';
import { serializedCoordinatesToCoordinates } from '../../utils/serializedCoordinates';
import { pillPopupOffset } from '../../utils/svgDefinitions/svgMarkerComponents';
import useSvgImages from '../../utils/useSvgImages';
import ActiveTrailPopup from './ActiveTrailPopup';
import CollapsedTrailPinLayer from './CollapsedTrailPinLayer';
import RegularTrailPinLayer from './RegularTrailPinLayer';

export type TrailPinsProps = {
  activityType?: TrailActivityType; // Used to display the correct trail card stats
  cardLocation?: TrailCardLocation;
  detailedCardLocation?: CardLocation;
  inspirationalListType?: InspirationalListType;
  inspirationalPageSlug?: string;
  mapLocation?: MapLocation | string; // Accept string for legacy PageStrings support
  onPinHover?: (trailIds: number[]) => void;
  sourceId: string;
  isFavorite?: (itemId: number) => boolean;
  isComplete?: (itemId: number) => boolean;
  isVerified?: (itemId: number) => boolean;
  handleFavoriteClick?: (itemId: number) => void;
  listModal?: React.ReactNode;
  signUpModal?: React.ReactNode;
} & Pick<ComponentProps<typeof ActiveTrailPopup>, 'onTrailCardViewed' | 'onTrailNavigate' | 'getCTAProps' | 'getTrailCardUrl'>;

const TrailPins = ({
  activityType,
  cardLocation,
  detailedCardLocation,
  inspirationalListType,
  inspirationalPageSlug,
  mapLocation,
  onPinHover,
  onTrailCardViewed,
  onTrailNavigate,
  sourceId,
  isFavorite,
  isComplete,
  isVerified,
  handleFavoriteClick,
  listModal,
  signUpModal,
  getCTAProps,
  getTrailCardUrl
}: TrailPinsProps) => {
  const dispatch = useAppDispatch();
  const viewedPinSerializedCoordinatesThisSession = useAppSelector(state => state.map.viewedPinSerializedCoordinatesThisSession);

  const { activeTrails, trailResults } = useAppSelector(state => ({
    activeTrails: state.map.activeTrails,
    trailResults: state.map.trailResults
  }));
  const hideTrailPins = useAppSelector(state => state.map.hideTrailPins);

  const [hoveredSerializedCoordinates, setHoveredSerializedCoordinates] = useState('');
  const [selectedSerializedCoordinates, setSelectedSerializedCoordinates] = useState('');

  const { onMouseEnter, onMouseLeave } = useTrailPinInteractions(
    selectedSerializedCoordinates,
    setHoveredSerializedCoordinates,
    setSelectedSerializedCoordinates,
    isFavorite,
    isComplete,
    cardLocation,
    mapLocation,
    onPinHover
  );
  const { activeTrailSerializedCoordinates } = useActiveTrailCoordinates(selectedSerializedCoordinates, setSelectedSerializedCoordinates);
  const { isShiftPressed } = useShiftKey();
  const isHoveredExpression: ExpressionSpecification = ['==', ['get', 'serializedCoordinates'], hoveredSerializedCoordinates || ''];
  const isSelectedExpression: ExpressionSpecification = [
    'any',
    ['==', ['get', 'serializedCoordinates'], selectedSerializedCoordinates || ''],
    ['==', ['get', 'isSelected'], true]
  ];
  const isViewedExpression: ExpressionSpecification = [
    'in',
    ['get', 'serializedCoordinates'],
    ['literal', viewedPinSerializedCoordinatesThisSession]
  ];
  const isActiveTrailExpression: ExpressionSpecification = ['any', ['==', ['get', 'serializedCoordinates'], activeTrailSerializedCoordinates || '']];
  const commonLayoutProps = buildCommonLayoutProps(isHoveredExpression, isSelectedExpression);

  const popup = useMemo(() => {
    if (!trailResults || activeTrails?.length !== 1 || !activeTrailSerializedCoordinates) {
      return null;
    }
    if (isShiftPressed) {
      return null;
    }

    // When the active trail is part of a selected multi trailhead, we show the carousel popup - otherwise, just show the trail card
    const trailsAtActiveTrailhead = trailResults[activeTrailSerializedCoordinates];
    const isSelectedMultiTrail = selectedSerializedCoordinates === activeTrailSerializedCoordinates && trailsAtActiveTrailhead?.length > 1;
    const initialTrailIndex = isSelectedMultiTrail ? trailsAtActiveTrailhead.findIndex(({ ID }) => ID === activeTrails[0].ID) : undefined;
    const trailsInPopup = isSelectedMultiTrail ? trailsAtActiveTrailhead : activeTrails;

    const { lat, lng } = serializedCoordinatesToCoordinates(activeTrailSerializedCoordinates);
    return (
      <ActiveTrailPopup
        activityType={activityType}
        cardLocation={cardLocation}
        detailedCardLocation={detailedCardLocation}
        inspirationalListType={inspirationalListType}
        inspirationalPageSlug={inspirationalPageSlug}
        initialTrailIndex={initialTrailIndex}
        isComplete={!isComplete ? undefined : id => isComplete(id)}
        isFavorite={!isFavorite ? undefined : id => isFavorite(id)}
        isVerified={!isVerified ? undefined : id => isVerified(id)}
        latitude={lat}
        longitude={lng}
        offset={pillPopupOffset}
        onFavoriteClick={!handleFavoriteClick ? undefined : id => handleFavoriteClick(id)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onTrailCardViewed={onTrailCardViewed}
        onTrailNavigate={onTrailNavigate}
        onTrailChange={trail => {
          dispatch(updateActiveTrails(trail ? [trail] : undefined));
        }}
        trails={trailsInPopup}
        getCTAProps={getCTAProps}
        getTrailCardUrl={getTrailCardUrl}
      />
    );
  }, [
    activeTrailSerializedCoordinates,
    activeTrails,
    activityType,
    cardLocation,
    detailedCardLocation,
    inspirationalListType,
    inspirationalPageSlug,
    dispatch,
    handleFavoriteClick,
    isShiftPressed,
    isComplete,
    isFavorite,
    isVerified,
    onMouseEnter,
    onMouseLeave,
    onTrailCardViewed,
    onTrailNavigate,
    selectedSerializedCoordinates,
    trailResults,
    getCTAProps,
    getTrailCardUrl
  ]);

  const svgDefinitions = useTrailPinSvgDefinitions();
  const { imagesAreLoaded } = useSvgImages(svgDefinitions);

  if (!imagesAreLoaded || hideTrailPins) {
    return null;
  }

  return (
    <>
      <CollapsedTrailPinLayer
        sourceId={sourceId}
        isHoveredExpression={isHoveredExpression}
        isSelectedExpression={isSelectedExpression}
        isViewedExpression={isViewedExpression}
        isActiveTrailExpression={isActiveTrailExpression}
        commonLayoutProps={commonLayoutProps}
      />
      <RegularTrailPinLayer
        sourceId={sourceId}
        isHoveredExpression={isHoveredExpression}
        isSelectedExpression={isSelectedExpression}
        isViewedExpression={isViewedExpression}
        isActiveTrailExpression={isActiveTrailExpression}
      />
      {popup}
      {listModal}
      {signUpModal}
    </>
  );
};

export default TrailPins;
